/**
 * Exit Alert Threshold Detection
 *
 * Calculates whether a trade position's price decline has exceeded
 * the investor's configured exit alert threshold.
 *
 * Validates: Requirements 6.1, 6.2, 6.3
 */

/**
 * Calculates the percentage drop from entry price to current price.
 * A positive result means the price fell; a negative result means the price rose.
 *
 * Formula: ((entryPrice - currentPrice) / entryPrice) * 100
 *
 * @param entryPrice - The original stock purchase price (must be > 0)
 * @param currentPrice - The current market price (must be >= 0)
 * @returns The percentage drop to 2 decimal places
 * @throws Error if entryPrice is not positive
 */
export function calculatePriceDropPercentage(entryPrice: number, currentPrice: number): number {
  if (entryPrice <= 0) {
    throw new Error('Entry price must be a positive number');
  }
  if (currentPrice < 0) {
    throw new Error('Current price must be non-negative');
  }

  const dropPercentage = ((entryPrice - currentPrice) / entryPrice) * 100;
  return Math.round(dropPercentage * 100) / 100;
}

/**
 * Determines whether an exit alert should be displayed based on the
 * price drop exceeding the investor's configured threshold.
 *
 * The alert fires when the percentage drop EXCEEDS the threshold (strictly greater than).
 *
 * @param entryPrice - The original stock purchase price (must be > 0)
 * @param currentPrice - The current market price (must be >= 0)
 * @param threshold - The exit alert threshold percentage (must be between 1 and 50 inclusive)
 * @returns true if the price drop percentage exceeds the threshold
 * @throws Error if threshold is not between 1 and 50, or if prices are invalid
 */
export function shouldShowExitAlert(
  entryPrice: number,
  currentPrice: number,
  threshold: number
): boolean {
  if (threshold < 1 || threshold > 50) {
    throw new Error('Exit alert threshold must be between 1 and 50 (percentage)');
  }

  const dropPercentage = calculatePriceDropPercentage(entryPrice, currentPrice);
  return dropPercentage > threshold;
}
